import classnames from "classnames";
import {
    Bike,
    Bus,
    Calendar,
    Car,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    Clock,
    Gift,
    History,
    Layers,
    LayoutDashboard,
    Megaphone,
    MessageSquare,
    QrCode,
    Ship,
    Star,
    Store,
    TrendingUp,
    UserCog,
    Users,
    Wrench
} from "lucide-react";
import React, { Component } from "react";
import { connect } from "react-redux";
import { NavLink, withRouter } from "react-router-dom";

import chillLogo from "../../assets/css/img/chillLogo.svg";
import SimpleTooltip from "../../components/common/SimpleTooltip";
import IntlMessages from "../../helpers/IntlMessages";

import {
    addContainerClassname,
    changeDefaultClassnames,
    changeSelectedMenuHasSubItems,
    setContainerClassnames,
} from "../../redux/actions";

import { SetActiveItem } from "../../redux/activeItem/actions";
import { setSearch } from "../../redux/settings/actions";

import menuItems from "../../constants/menu";

// Icon mapping from iconsmind to lucide-react
// Maps old iconmind/simple-icon class names to appropriate Lucide React icons
const iconMap = {
    "iconsminds-monitor-analytics": LayoutDashboard, // Dashboard (default)
    "iconsminds-shop": Store, // Service Providers
    "iconsminds-engineering": UserCog, // Workers (better icon for workers/employees)
    "iconsminds-calendar-4": Calendar, // Requests, Appointments
    "simple-icon-speech": MessageSquare, // Chat
    "simple-icon-clock": Clock, // Subscriptions, Orders (better icon for subscriptions/payments/orders)
    "iconsminds-wrench": Wrench, // Services (default), Up Selling will be overridden
    "iconsminds-loudspeaker": Megaphone, // Marketing
    "simple-icon-people": Users, // Customers (CRM submenu)
    "iconsminds-qr-code": QrCode, // Promocodes
    "simple-icon-layers": Layers, // Banners
    "simple-icon-star": Star, // Featured Providers
    "iconsminds-gift-box": Gift, // Vouchers / Compensations, Marketing Vouchers
    "iconsminds-car": Car, // Vehicles, Cars
    "iconsminds-motorcycle": Bike, // Bikes
    "iconsminds-bus-2": Bus, // Caravans
    "iconsminds-yacht": Ship, // Boats
};

// Special handling for sections that need different icons based on context
const getSectionIcon = (itemId, iconName) => {
    // Handle special cases where same icon class is used for different sections
    if (iconName === "iconsminds-monitor-analytics") {
        if (itemId === "CRMmenu") {
            return Users; // CRM should use Users icon
        } else if (itemId === "orderHistory") {
            return History; // Order History should use History icon
        }
        return LayoutDashboard; // Default to Dashboard for dashboard
    }

    if (iconName === "iconsminds-wrench") {
        if (itemId === "up selling") {
            return TrendingUp; // Up Selling uses TrendingUp
        }
        return Wrench; // Services and default uses Wrench
    }

    // Use the icon from the map
    return iconMap[iconName];
};

const getLucideIcon = (iconName, className = "", itemId = null) => {
    // Use special section icon handler if itemId is provided
    const IconComponent = itemId ? getSectionIcon(itemId, iconName) : iconMap[iconName];
    return IconComponent ? <IconComponent className={className} /> : null;
};

class ModernSidebar extends Component {
    constructor(props) {
        super(props);
        this.state = {
            menuItems: [],
            expandedItems: [],
            collapsed: false,
            isMobile: false,
            mobileOpen: false,
        };
        this.handleResize = this.handleResize.bind(this);
        this.handleClickOutside = this.handleClickOutside.bind(this);
    }

    componentDidMount() {
        const { authUser } = this.props;
        const { accessType } = authUser && authUser.user;
        const filtered = menuItems.filter((item) => item.role.includes(accessType));

        // Check if mobile on mount
        const isMobile = window.innerWidth <= 991;
        this.setState({
            menuItems: filtered,
            isMobile
        }, () => {
            this.setActiveExpanded();
        });

        // Initialize sidebar width - set initial margin with spacing for main and TopNav (desktop only)
        if (!isMobile) {
            setTimeout(() => {
                const appContainer = document.getElementById('app-container');
                const mainElement = appContainer?.querySelector('main');
                const topNavElement = appContainer?.querySelector('.navbar.fixed-top');

                if (mainElement) {
                    mainElement.style.marginLeft = 'calc(260px + 60px)';
                    mainElement.style.transition = 'margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
                }

                if (topNavElement) {
                    topNavElement.style.marginLeft = 'calc(260px)';
                    topNavElement.style.width = 'calc(100% - 260px)';
                    topNavElement.style.transition = 'margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1), width 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
                }
            }, 100);
            this.updateAppContainerClass();
        }

        // Add event listeners
        window.addEventListener('resize', this.handleResize);
        document.addEventListener('mousedown', this.handleClickOutside);

        // Listen for mobile menu toggle events from TopNav
        window.addEventListener('toggleMobileSidebar', this.toggleMobileSidebar);

        // Expose toggle function globally for TopNav
        window.toggleModernSidebar = this.toggleMobileSidebar;
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.handleResize);
        document.removeEventListener('mousedown', this.handleClickOutside);
        window.removeEventListener('toggleMobileSidebar', this.toggleMobileSidebar);
        delete window.toggleModernSidebar;
        // Clean up body and html classes on unmount
        document.body.classList.remove('sidebar-mobile-open');
        document.documentElement.classList.remove('sidebar-mobile-open');
    }

    handleResize = () => {
        const isMobile = window.innerWidth <= 991;
        if (this.state.isMobile !== isMobile) {
            // Clean up body class when switching between mobile/desktop
            document.body.classList.remove('sidebar-mobile-open');
            this.setState({ isMobile, mobileOpen: false });
            // Reset margins on mobile
            if (isMobile) {
                const appContainer = document.getElementById('app-container');
                const mainElement = appContainer?.querySelector('main');
                const topNavElement = appContainer?.querySelector('.navbar.fixed-top');
                if (mainElement) mainElement.style.marginLeft = '0';
                if (topNavElement) {
                    topNavElement.style.marginLeft = '0';
                    topNavElement.style.width = '100%';
                }
            }
        }
    };

    handleClickOutside = (event) => {
        if (this.state.isMobile && this.state.mobileOpen) {
            const sidebar = document.querySelector('.modern-sidebar');
            const toggleBtn = document.querySelector('.mobile-menu-toggle');
            if (sidebar && !sidebar.contains(event.target) && !toggleBtn?.contains(event.target)) {
                this.setState({ mobileOpen: false });
            }
        }
    };

    toggleMobileSidebar = () => {
        this.setState(prevState => ({ mobileOpen: !prevState.mobileOpen }));
    };

    componentDidUpdate(prevProps, prevState) {
        if (prevState.collapsed !== this.state.collapsed && !this.state.isMobile) {
            this.updateAppContainerClass();
        }
        if (this.props.location.pathname !== prevProps.location.pathname) {
            this.setActiveExpanded();
            // Close mobile sidebar on navigation
            if (this.state.isMobile && this.state.mobileOpen) {
                this.setState({ mobileOpen: false });
            }
        }
        if (this.props.authUser !== prevProps.authUser) {
            const filtered = menuItems.filter((item) =>
                item?.role?.includes(this.props.authUser?.user?.accessType)
            );
            this.setState({ menuItems: filtered });
        }
        // Prevent body scroll when mobile sidebar is open
        if (prevState.mobileOpen !== this.state.mobileOpen) {
            if (this.state.isMobile) {
                if (this.state.mobileOpen) {
                    document.body.classList.add('sidebar-mobile-open');
                    document.documentElement.classList.add('sidebar-mobile-open');
                } else {
                    document.body.classList.remove('sidebar-mobile-open');
                    document.documentElement.classList.remove('sidebar-mobile-open');
                }
            }
        }
    }

    updateAppContainerClass = () => {
        // Use setTimeout to ensure DOM is updated
        setTimeout(() => {
            const appContainer = document.getElementById('app-container');
            const mainElement = appContainer?.querySelector('main');
            const topNavElement = appContainer?.querySelector('.navbar.fixed-top');

            if (appContainer && mainElement && !this.state.isMobile) {
                if (this.state.collapsed) {
                    appContainer.classList.add('sidebar-collapsed');
                    mainElement.style.marginLeft = 'calc(80px + 60px)';
                    if (topNavElement) {
                        topNavElement.style.marginLeft = 'calc(80px)';
                        topNavElement.style.width = 'calc(100% - 80px)';
                    }
                } else {
                    appContainer.classList.remove('sidebar-collapsed');
                    mainElement.style.marginLeft = 'calc(260px + 60px)';
                    if (topNavElement) {
                        topNavElement.style.marginLeft = 'calc(260px)';
                        topNavElement.style.width = 'calc(100% - 260px)';
                    }
                }
            }
        }, 0);
    };

    setActiveExpanded = () => {
        const { menuItems } = this.state;
        menuItems.forEach((item) => {
            if (item.subs && this.isGroupActive(item)) {
                this.setState({ expandedItems: [item.id] });
            }
        });
    };

    isActive = (url) => {
        const { location } = this.props;
        if (!url) return false;
        return location.pathname === url || location.pathname.startsWith(url + "/");
    };

    isGroupActive = (item) => {
        if (item.subs) {
            return item.subs.some((subItem) => this.isActive(subItem.to));
        }
        return false;
    };

    toggleExpanded = (itemId) => {
        this.setState((prevState) => ({
            expandedItems: prevState.expandedItems.includes(itemId)
                ? []
                : [itemId],
        }));
    };

    toggleCollapse = () => {
        this.setState((prev) => ({ collapsed: !prev.collapsed }));
    };

    handleMenuClick = () => {
        const { SetActiveItem, setSearch } = this.props;
        SetActiveItem(false);
        setSearch("");
        // Close mobile sidebar when menu item is clicked
        if (this.state.isMobile && this.state.mobileOpen) {
            this.setState({ mobileOpen: false });
        }
    };

    render() {
        const { bookings, newChatsCount, openComplaintsCount, authUser } = this.props;
        const { menuItems, expandedItems, collapsed, isMobile, mobileOpen } = this.state;

        const filteredBookings = Object.values(bookings || {}).filter(
            (item) => item.status === "new" || item.reassigning === true
        );
        const delayedBookings = Object.values(bookings || {}).filter(
            (item) => item.delayed
        );

        return (
            <>
                {/* Mobile overlay backdrop */}
                {isMobile && mobileOpen && (
                    <div
                        className="sidebar-overlay"
                        onClick={() => this.setState({ mobileOpen: false })}
                    />
                )}

                <div className={classnames("modern-sidebar", {
                    collapsed: !isMobile && collapsed,
                    'mobile-open': isMobile && mobileOpen
                })}>
                    {/* Header */}
                    <div className="modern-sidebar-header">
                        <div className="sidebar-logo">
                            <img src={chillLogo} alt="Chill Logo" className="sidebar-logo-img" />
                        </div>
                        {!collapsed && (
                            <div className="sidebar-brand">
                                <h1 className="p-0 m-0">Chill Admin</h1>
                                <p>Admin Portal</p>
                            </div>
                        )}
                        {isMobile && (
                            <button className="toggle-btn" onClick={() => this.setState({ mobileOpen: false })}>
                                <ChevronLeft className="icon" />
                            </button>
                        )}
                    </div>

                    {/* Content */}
                    <div className="modern-sidebar-content">
                        {/* <PerfectScrollbar option={{ suppressScrollX: true, wheelPropagation: false }}> */}
                        <div className="menu-items">
                            {menuItems.map((item, index = 0) => {
                                const hasSubItems = item.subs && item.subs.length > 0;
                                const isActive = this.isActive(item.to);
                                const isGroupActive = this.isGroupActive(item);
                                const isExpanded = expandedItems.includes(item.id);
                                const IconComponent = getLucideIcon(item.icon, "", item.id);

                                // Calculate badge count
                                let badgeCount = 0;
                                if (item.label === "Requests" && filteredBookings?.length > 0) {
                                    badgeCount = filteredBookings.length;
                                } else if (item.label === "Appointments" && delayedBookings?.length > 0) {
                                    badgeCount = delayedBookings.length;
                                } else if (item.label === "Chat" && newChatsCount > 0) {
                                    badgeCount = newChatsCount > 99 ? "99+" : newChatsCount;
                                } else if (item.label === "Rated Orders" && openComplaintsCount > 0) {
                                    badgeCount = openComplaintsCount > 99 ? "99+" : openComplaintsCount; // open low-rating complaints
                                }

                                // Prepare tooltip content
                                const tooltipContent = collapsed ? (
                                    hasSubItems ? (
                                        <div className="tooltip-with-subitems">
                                            <div className="tooltip-title">
                                                <IntlMessages id={item.label} />
                                            </div>
                                            <div className="tooltip-subitems">
                                                {item.subs
                                                    ?.filter((sub) => sub?.role?.includes(authUser?.user?.accessType))
                                                    .map((subItem, idx) => {
                                                        const subActive = this.isActive(subItem.to);
                                                        return (
                                                            <NavLink
                                                                key={idx}
                                                                to={subItem.to}
                                                                onClick={(e) => {
                                                                    this.handleMenuClick();
                                                                    // Close tooltip on click
                                                                    if (window.closeTooltip) {
                                                                        window.closeTooltip();
                                                                    }
                                                                }}
                                                                className={classnames("tooltip-subitem", { active: subActive })}
                                                            >
                                                                <IntlMessages id={subItem.label} />
                                                            </NavLink>
                                                        );
                                                    })}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="tooltip-simple">
                                            <IntlMessages id={item.label} />
                                        </div>
                                    )
                                ) : null;

                                return (
                                    <div key={item.id} className="menu-item-container">
                                        {hasSubItems ? (
                                            <>
                                                <SimpleTooltip
                                                    content={tooltipContent}
                                                    show={collapsed}
                                                >
                                                    <button
                                                        className={classnames("menu-item", {
                                                            active: isGroupActive,
                                                            collapsed,
                                                        })}
                                                        onClick={() => this.toggleExpanded(item.id)}
                                                    >
                                                        {(isActive || isGroupActive) && <div className="active-indicator" />}
                                                        <span className="menu-icon">
                                                            {IconComponent || <i className={item.icon} />}
                                                        </span>
                                                        {!collapsed && (
                                                            <>
                                                                <span className="menu-label">
                                                                    <IntlMessages id={item.label} />
                                                                </span>
                                                                <ChevronDown
                                                                    className={classnames("chevron", { rotated: isExpanded })}
                                                                />
                                                            </>
                                                        )}
                                                    </button>
                                                </SimpleTooltip>
                                                {isExpanded && !collapsed && (
                                                    <div className="submenu">
                                                        {item.subs
                                                            ?.filter((sub) => sub?.role?.includes(authUser?.user?.accessType))
                                                            .map((subItem, index) => {
                                                                const subActive = this.isActive(subItem.to);
                                                                const SubIcon = getLucideIcon(subItem.icon, "", subItem.id || item.id);
                                                                return (
                                                                    <NavLink
                                                                        key={`${item.id}_${index}`}
                                                                        to={subItem.to}
                                                                        onClick={this.handleMenuClick}
                                                                        className={classnames("submenu-item", { active: subActive })}
                                                                    >
                                                                        <span className="submenu-icon">
                                                                            {SubIcon || <i className={subItem.icon} />}
                                                                        </span>
                                                                        <span className="submenu-label">
                                                                            <IntlMessages id={subItem.label} />
                                                                        </span>
                                                                    </NavLink>
                                                                );
                                                            })}
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <SimpleTooltip
                                                content={tooltipContent}
                                                show={collapsed}
                                            >
                                                <NavLink
                                                    to={item.to}
                                                    onClick={this.handleMenuClick}
                                                    className={classnames("menu-item", {
                                                        active: isActive,
                                                        collapsed,
                                                    })}
                                                >
                                                    {isActive && <div className="active-indicator" />}
                                                    <span className="menu-icon">
                                                        {IconComponent || <i className={item.icon} />}
                                                    </span>
                                                    {!collapsed && (
                                                        <>
                                                            <span className="menu-label">
                                                                <IntlMessages id={item.label} />
                                                            </span>
                                                            {badgeCount > 0 && (
                                                                <span className="badge">{badgeCount}</span>
                                                            )}
                                                        </>
                                                    )}
                                                    {collapsed && badgeCount > 0 && (
                                                        <span className="badge-collapsed">{badgeCount > 9 ? "9+" : badgeCount}</span>
                                                    )}
                                                </NavLink>
                                            </SimpleTooltip>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        {/* </PerfectScrollbar> */}
                    </div>

                    {/* Footer */}
                    {!isMobile && (
                        <div className="modern-sidebar-footer">
                            <button className="toggle-btn" onClick={this.toggleCollapse}>
                                {collapsed ? <ChevronRight className="icon" size={40} /> : <ChevronLeft className="icon" size={40} />}
                            </button>
                        </div>
                    )}
                </div>
            </>
        );
    }
}

const mapStateToProps = ({ menu, authUser, providers, chatApp }) => {
    const {
        containerClassnames,
        subHiddenBreakpoint,
        menuHiddenBreakpoint,
        menuClickCount,
        selectedMenuHasSubItems,
    } = menu;
    return {
        containerClassnames,
        subHiddenBreakpoint,
        menuHiddenBreakpoint,
        menuClickCount,
        selectedMenuHasSubItems,
        authUser,
        bookings: providers ? providers.bookings : {},
        newMessagesCount: chatApp?.newMessagesCount || 0,
        newChatsCount: chatApp?.newChatsCount || 0,
        openComplaintsCount: chatApp?.openComplaintsCount || 0,
    };
};

export default withRouter(
    connect(mapStateToProps, {
        setContainerClassnames,
        addContainerClassname,
        changeDefaultClassnames,
        changeSelectedMenuHasSubItems,
        SetActiveItem,
        setSearch,
    })(ModernSidebar)
);
